import { useEffect, useRef } from 'react';
import { useRoute } from '@react-navigation/native';
import { showError, showInfo } from '../../../shared/ui/appToast';
import { getProductById } from '../../../catalog/infra-api';
import {
  getStockByProductAndBranch,
  isInventoriableProduct,
  type StockRow,
} from '../../../inventory/stock/public-api';
import type { Product } from '../../../catalog/domain-api';
import { isDatabaseUnavailableError } from '../../../shared/localDb';

type Params = {
  currentBranchId: string | null | undefined;
  openAdjustModal: (row: StockRow) => void;
  openAddStockModalForProduct: (product: Product) => void;
  openAddStockModal: () => void;
};

export function useInventoryRouteFocus({
  currentBranchId,
  openAdjustModal,
  openAddStockModalForProduct,
  openAddStockModal,
}: Params) {
  const route = useRoute<any>();
  const consumedFocusTokenRef = useRef<string | null>(null);

  useEffect(() => {
    const params = route.params as
      | {
          focusToken?: string;
          focusProductId?: string;
          focusProductName?: string | null;
          focusProductSku?: string | null;
          preferredAction?: 'adjust' | 'add';
          /** Desde Inicio → «Ingresar stock»: abre el modal sin producto prefijado. */
          openAddStock?: boolean;
        }
      | undefined;

    if (!params?.focusToken || params.focusToken === consumedFocusTokenRef.current) {
      return;
    }

    consumedFocusTokenRef.current = params.focusToken;

    if (params.openAddStock === true && !params.focusProductId) {
      openAddStockModal();
      return;
    }

    const run = async () => {
      if (!currentBranchId || !params.focusProductId) {
        return;
      }

      const stockRow = await getStockByProductAndBranch(currentBranchId, params.focusProductId);
      const product =
        (await getProductById(params.focusProductId)) ??
        (params.focusProductName
          ? ({
              id: params.focusProductId,
              name: params.focusProductName,
              price: 0,
              sku: params.focusProductSku ?? params.focusProductId,
              barcode: params.focusProductSku ?? null,
              categoryId: null,
            } as Product)
          : null);

      if (!product) {
        showError('Producto no encontrado', 'No pudimos ubicar el producto para abrirlo en Stock.');
        return;
      }
      if (!isInventoriableProduct(product)) {
        showInfo('Servicio sin stock', 'Los servicios no llevan stock.');
        return;
      }

      if (params.preferredAction === 'adjust' && stockRow) {
        openAdjustModal({
          id: stockRow.id,
          productId: stockRow.productId,
          branchId: stockRow.branchId,
          productName: product.name,
          productSku: product.sku,
          productBarcode: product.barcode ?? product.sku,
          productPrice: product.price,
          unitOfMeasure: product.unitOfMeasure ?? 'unit',
          quantity: stockRow.quantity,
          avgCost: stockRow.avgCost,
          minQuantity: stockRow.minQuantity,
          expiresAt: stockRow.expiresAt,
        });
        return;
      }

      openAddStockModalForProduct(product);
    };

    run().catch((error) => {
      if (!isDatabaseUnavailableError(error)) {
        console.warn('[Inventory] No se pudo abrir el foco solicitado desde POS', error);
      }
    });
  }, [currentBranchId, openAddStockModal, openAddStockModalForProduct, openAdjustModal, route.params]);
}
